import React, { useCallback, useId } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
// eslint-disable-next-line venom/no-venom-api-calls
import { VenomApi } from 'client/data/api/api-client';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { useValidatedInput } from 'site-modules/shared/components/inventory/use-validated-input/use-validated-input';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { Button, Form } from 'reactstrap';
import { Input } from 'site-modules/shared/components/inventory/input/input';
import { ErrorFieldCopy } from 'site-modules/shared/components/lead-form/error-field-copy/error-field-copy';

import './wizard-zip-form.scss';

const getZipCodeValue = (isInternational, zipCode, zipFromAnswers) => {
  if (zipFromAnswers) return zipFromAnswers;
  return isInternational ? '' : zipCode;
};

export const WizardZipForm = ({ isInternational = false, zipCode, onAnswer, zipFromAnswers = undefined }) => {
  const [inProgress, toggleProgress] = useToggle(false);
  const errorId = useId();

  const asyncValidateHandler = useCallback(async newZipCode => {
    // eslint-disable-next-line venom/no-venom-api-calls
    await VenomApi.fetchJson(`/location/zip/${newZipCode}`, { credentials: 'same-origin' });
  }, []);

  const { isValid, inputProps, validate } = useValidatedInput({
    value: getZipCodeValue(isInternational, zipCode, zipFromAnswers),
    onSyncValidate: validation.validateZip,
    onAsyncValidate: asyncValidateHandler,
    onBeforeValidate: toggleProgress,
    onAfterValidate: toggleProgress,
    onValidateSuccess: onAnswer,
  });

  const handleSubmit = useCallback(
    e => {
      e.preventDefault();
      validate();
    },
    [validate]
  );

  return (
    <>
      <Form
        id="zip-wizard-question-form"
        noValidate
        className="pos-r zip-wizard-form"
        name="form"
        onSubmit={handleSubmit}
      >
        <div className="pos-r">
          <Input
            {...inputProps}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="Enter ZIP"
            aria-label="Zip Code"
            name="zip"
            inputClassName="text-start"
            startAdornment={<i className="icon-location size-20 text-cool-gray-30" aria-hidden />}
            data-tracking-id="provide_zip_code"
            invalid={!isValid}
            aria-describedby={errorId}
          />
          <Button
            color="blue-50"
            size="sm"
            type="submit"
            className="submit-btn pos-a d-flex justify-content-center align-items-center rounded-6"
            aria-label="Submit"
            disabled={inProgress}
          >
            <span className="icon-arrow-right8 icon text-white size-16" aria-hidden />
          </Button>
        </div>

        <ErrorFieldCopy
          id={errorId}
          className={classnames('text-danger small', { 'mt-0_25': !isValid })}
          copy={isValid ? '' : 'Please enter a valid ZIP code.'}
        />
      </Form>
    </>
  );
};

WizardZipForm.propTypes = {
  isInternational: PropTypes.bool,
  zipCode: PropTypes.string.isRequired,
  zipFromAnswers: PropTypes.string,
  onAnswer: PropTypes.func.isRequired,
};
